import React, { useState, useEffect, useCallback } from "react";
import "./DashboardHome.scss";
import api from "../../../config/configApi";
import * as Photos from "../../../services/photos";
import DashboardBox from "../../../components/Dashboard/DashboardBox";
import {
  MdOutlineStar,
  MdOutlineTrendingUp,
  MdPointOfSale,
  MdLocalGroceryStore,
  MdMoreVert,
  MdOutlineUpdate,
  MdVisibility,
  MdEdit,
  MdDelete,
} from "react-icons/md";
import { PieChart } from "@mui/x-charts/PieChart";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
  Typography,
} from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { formatKm, formatValor, localeText } from "../../../utils/webConstants";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import moment from "moment";
import { logError, logUsuarioForm } from "../../../services/logService";
import { useAuth } from "../../../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

const PERIOD_OPTIONS = [
  { label: "último dia", value: 1 },
  { label: "última semana", value: 2 },
  { label: "útimo mês", value: 3 },
  { label: "último ano", value: 4 },
];

function DashboardHome() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // ESTADOS PARA LISTA DE OFERTAS
  const [loading, setLoading] = useState(false);
  const [offerList, setOfferList] = useState([]);
  const [errorOfferList, setErrorOfferList] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 12,
  });
  const [rowCount, setRowCount] = useState(0); // Total de itens
  const pageSizeOptions = [5, 10, 12, 25, 50];

  // ESTADOS PARA DELETAR VEICULO
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [firebaseDeleteError, setFirebaseDeleteError] = useState(false);

  // ESTADOS DO GRAPHBOX E HANDLERS
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [errorChartData, setErrorChartData] = useState(null);
  const [selectedOption, setSelectedOption] = useState(1);
  const [totalViewsSum, setTotalViewsSum] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const openGraph = Boolean(anchorEl);
  const ITEM_HEIGHT = 48;

  const handleClickGraph = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseGraph = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (option) => {
    setSelectedOption(option);
    handleCloseGraph();
  };

  const handlePieItemClick = (event, itemDetails) => {
    const { dataIndex } = itemDetails;
    const clickedItemData = chartData[dataIndex];

    if (clickedItemData && clickedItemData.id) {
      const veiculoId = clickedItemData.id;
      navigate(`/dashboard/anuncio/${veiculoId}/detalhes`);
    }
  };

  // ESTADOS E HANDLERS DO DIALOG
  const [open, setOpen] = useState(false);

  const handleClickOpen = (veiculo) => {
    if (user.deletar !== true) {
      toast("Seu usuário não possui permissão de deletar!", {
        icon: "⚠️",
        style: { background: "#fff4e5", color: "#663c00" },
      });
    } else {
      setVehicleToDelete(veiculo);
      setFirebaseDeleteError(false);
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setVehicleToDelete(null);
  };

  const fetchData = useCallback(async () => {
    setLoadingGraph(true);
    setErrorChartData(null);
    setTotalViewsSum(0);

    try {
      // A rota para os veículos mais visualizados sempre terá o /:option
      const response = await api.get(
        `/api/visualizacoes/top-tres/${selectedOption}`,
      );
      const rawData = response.data; // O array que você recebeu do backend

      // Mapear os dados do backend para o formato do PieChart
      const formattedData = rawData.map((item, index) => ({
        // Use item.veiculo.id para o id do gráfico, ou index se for apenas para unicidade do MUI
        id: item.veiculo.id || index, // Prefer veiculo.id para referência única, fallback para index
        value: item.total_visualizacoes,
        label: `${item.veiculo.modelo} (ID: ${item.veiculo.id})`,
      }));

      // Calcula a soma total das visualizações
      const sum = rawData.reduce(
        (acc, item) => acc + item.total_visualizacoes,
        0,
      ); // <--- CÁLCULO DA SOMA
      setTotalViewsSum(sum);

      setChartData(formattedData);
    } catch (err) {
      setErrorChartData("Erro ao carregar dados do gráfico.");
      setChartData([]); // Limpa os dados em caso de erro
    } finally {
      setLoadingGraph(false);
    }
  }, [selectedOption]);

  // LISTA DE OFERTAS
  useEffect(() => {
    const fetchOffersList = async () => {
      setLoading(true);
      try {
        // Envie os parâmetros de paginação para o backend
        const response = await api.get(
          `/api/anuncios?orderBy=id&orderDirection=asc&oferta=true&status=true&limiteImagens=1&visualizacoes=true&page=${paginationModel.page}&pageSize=${paginationModel.pageSize}`,
        );
        setOfferList(response.data.data); // Backend retorna { data: [...], total: N }
        setRowCount(response.data.total); // Backend envia o total de itens
      } catch (err) {
        const errMessage =
          err.response?.data?.msg ||
          err.message ||
          "Erro desconhecido ao carregar lista de ofertas.";
        setErrorOfferList(errMessage);
        setOfferList([]); // Limpa a lista de ofertas em caso de erro
      } finally {
        setLoading(false);
      }
    };

    fetchOffersList();
  }, [paginationModel]);

  // DATA DO GRAFICO
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = [
    {
      field: "actions",
      headerName: "AÇÃO",
      width: 160,
      display: "flex",
      justify: "center",
      type: "actions",
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            className="actionButton secondary"
            color="secondary"
            aria-label="ver"
            onClick={() => handleNavigateDetails(params.row.id)}
          >
            <MdVisibility />
          </Button>
          <Button
            className="actionButton success"
            color="success"
            aria-label="editar"
            onClick={() => handleNavigateEdit(params.row.id)}
          >
            <MdEdit />
          </Button>
          <Button
            className="actionButton error"
            color="error"
            aria-label="excluir"
            onClick={() => handleClickOpen(params.row)}
          >
            <MdDelete />
          </Button>
          <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              Confirmar Exclusão do Anúncio?
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                Você tem certeza que deseja excluir o anúncio do veículo #
                {vehicleToDelete?.id}
                {vehicleToDelete && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <img
                      src={vehicleToDelete.imagens[0]?.ftp_url}
                      alt={vehicleToDelete.modelo}
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: "4px",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/default.jpg";
                      }} // Fallback para imagem
                    />
                    {vehicleToDelete.categoria.id === 1 && (
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {vehicleToDelete.marca.nome} {vehicleToDelete.modelo}{" "}
                        {vehicleToDelete.tracao}{" "}
                        {vehicleToDelete.ano_fabricacao}/
                        {vehicleToDelete.ano_modelo} {vehicleToDelete.cambio}
                        <br />
                        {formatKm(vehicleToDelete.km)}{" "}
                        {formatValor(vehicleToDelete.valor)}
                      </Typography>
                    )}
                    {vehicleToDelete.categoria.id === 2 && (
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {vehicleToDelete.marca.nome} {vehicleToDelete.tipo}{" "}
                        {vehicleToDelete.modelo}{" "}
                        {vehicleToDelete.ano_fabricacao}/
                        {vehicleToDelete.ano_modelo}{" "}
                        {formatValor(vehicleToDelete.valor)}
                      </Typography>
                    )}
                    {vehicleToDelete.categoria.id === 3 && (
                      <Typography
                        variant="body2"
                        sx={{ textTransform: "uppercase" }}
                      >
                        {vehicleToDelete.marca.nome} {vehicleToDelete.modelo}{" "}
                        {vehicleToDelete.ano_fabricacao}/
                        {vehicleToDelete.ano_modelo} {vehicleToDelete.cambio}
                        <br />
                        {formatKm(vehicleToDelete.km)}{" "}
                        {formatValor(vehicleToDelete.valor)}
                      </Typography>
                    )}
                  </Box>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between" }}>
              <Button color="inherit" variant="contained" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                color="error"
                variant="contained"
                onClick={handleConfirmDelete}
                autoFocus
              >
                Confirmar
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      ),
    },
    { field: "id", headerName: "ID", width: 70, type: "number" },
    {
      field: "veiculo",
      headerName: "VEÍCULO",
      width: 432, // Aumente a largura para acomodar imagem e texto
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <img
            src={
              params.row.imagens[0]?.ftp_url ||
              `https://placehold.co/50x50/ADD8E6/000000?text=${params.row.modelo}`
            }
            alt={params.row.modelo}
            style={{
              width: 50,
              height: 50,
              borderRadius: "4px",
              objectFit: "cover",
            }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/default.jpg";
            }} // Fallback para imagem
          />
          {params.row.categoria.id === 1 && (
            <Typography variant="body2" sx={{ textTransform: "uppercase" }}>
              {params.row.modelo} {params.row.tracao}{" "}
              {params.row.ano_fabricacao}/{params.row.ano_modelo}{" "}
              {params.row.cambio}
            </Typography>
          )}
          {params.row.categoria.id === 2 && (
            <Typography variant="body2" sx={{ textTransform: "uppercase" }}>
              {params.row.tipo} {params.row.modelo} {params.row.ano_fabricacao}/
              {params.row.ano_modelo}
            </Typography>
          )}
          {params.row.categoria.id === 3 && (
            <Typography variant="body2" sx={{ textTransform: "uppercase" }}>
              {params.row.modelo} {params.row.ano_fabricacao}/
              {params.row.ano_modelo} {params.row.cambio}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "categoria",
      headerName: "CATEGORIA",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => params.row.categoria?.nome,
    },
    {
      field: "marca",
      headerName: "MARCA",
      width: 150,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => params.row.marca?.nome,
    },
    {
      field: "km",
      headerName: "KM",
      width: 130,
      renderCell: (params) =>
        params.row.categoria.id !== 2 ? formatKm(params.row.km) : "N/A",
    },
    {
      field: "valor",
      headerName: "VALOR",
      width: 130,
      renderCell: (params) => formatValor(params.row.valor),
    },
    {
      field: "created_at",
      headerName: "CRIADO",
      width: 130,
      type: "datetime",
      renderCell: (params) =>
        moment.utc(params.row.created_at).format("DD/MM/YYYY HH:mm:ss"),
    },
    {
      field: "updated_at",
      headerName: "ATUALIZADO",
      width: 130,
      type: "datetime",
      renderCell: (params) =>
        params.row.updated_at !== null
          ? moment.utc(params.row.updated_at).format("DD/MM/YYYY HH:mm:ss")
          : null,
    },
    {
      field: "total_visualizacoes",
      headerName: "VISUALIZAÇÕES",
      width: 130,
      type: "number",
      renderCell: (params) => params.row.total_visualizacoes,
    },
    {
      field: "oferta",
      headerName: "OFERTA",
      width: 130,
      type: "boolean",
      renderCell: (params) => (
        <Switch
          checked={params.row.oferta}
          inputProps={{ "aria-label": "controlled" }}
          onChange={(e) => handleOfertaChange(e, params.row.id)}
        />
      ),
    },
  ];

  const handleNavigateDetails = (veiculoId) => {
    if (user.visualizar !== true) {
      toast("Seu usuário não possui permissão de visualizar!", {
        icon: "⚠️",
        style: { background: "#fff4e5", color: "#663c00" },
      });
    } else {
      // logClickView(veiculoId);
      navigate(`/dashboard/anuncio/${veiculoId}/detalhes`);
    }
  };

  const handleNavigateEdit = (veiculoId) => {
    if (user.atualizar !== true) {
      toast("Seu usuário não possui permissão de editar!", {
        icon: "⚠️",
        style: { background: "#fff4e5", color: "#663c00" },
      });
    } else {
      navigate(`/dashboard/anuncio/${veiculoId}/editar`);
    }
  };

  const handleOfertaChange = async (event, veiculoId) => {
    if (user.atualizar !== true) {
      toast("Seu usuário não possui permissão de editar!", {
        icon: "⚠️",
        style: { background: "#fff4e5", color: "#663c00" },
      });
    } else {
      const novoStatus = event.target.checked;

      setOfferList((prevvehicleList) =>
        prevvehicleList.map((veiculo) =>
          veiculo.id === veiculoId
            ? { ...veiculo, oferta: novoStatus }
            : veiculo,
        ),
      );

      const toastId = toast.loading("Atualizando oferta do veículo...");

      setLoading(true);
      try {
        const response = await api.patch(`/api/anuncio/${veiculoId}`, {
          oferta: novoStatus,
        });
        toast.success("Oferta do veículo atualizada!", { id: toastId });
        await logUsuarioForm({
          email: user?.email || "N/A",
          formulario: `/dashboard/anuncio/${veiculoId}/editar/oferta`,
        });
      } catch (err) {
        const errorMessage =
          err.response?.data?.msg || err.message || "Erro desconhecido";
        if (err.status === 400 || err.status === 403 || err.status === 404) {
          toast(`${errorMessage}`, {
            id: toastId,
            icon: "⚠️",
            style: { background: "#fff4e5", color: "#663c00" },
          });
        } else {
          toast.error(
            `Erro inesperado ao atualizar oferta do veículo #${veiculoId}`,
            { id: toastId },
          );
        }
        setOfferList((prevvehicleList) =>
          prevvehicleList.map((veiculo) =>
            veiculo.id === veiculoId
              ? { ...veiculo, oferta: !novoStatus }
              : veiculo,
          ),
        );
        await logError({
          email: user?.email || "N/A",
          log_erro: `Erro insperado ao atualizar Oferta do Veículo ${veiculoId}: ${errorMessage}`,
          formulario: location.pathname,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Função que será chamada ao confirmar a exclusão
  const handleConfirmDelete = async () => {
    if (vehicleToDelete.id) {
      handleClose(); // Fecha o dialog imediatamente ou após o sucesso do toast
      let hasViews = true;
      let hasCriticalError = false;
      const toastId = toast.loading("Iniciando exclusão do veículo...");

      // 1. BUSCANDO AS IMAGENS DO VEICULO NO BANCO
      // let imagesToDeleteBackend = [];
      // try {
      //     const images = await api.get(`/api/imagens/${vehicleToDelete.id}`);
      //     if (images.status === 200) {
      //         imagesToDeleteBackend = images.data;
      //     } else {
      //         const errorMessage = images.response?.data?.msg || images.message || 'Erro desconhecido ao buscar imagens do veículo.';
      //         toast.error(`Erro ao buscar imagens do veículo #${vehicleToDelete.id}: ${errorMessage}`, { id: toastId });
      //         await logError({
      //             email: user?.email || 'N/A',
      //             log_erro: `Erro ao buscar imagens do veículo #${vehicleToDelete.id}: ${errorMessage}`,
      //             formulario: location?.pathname || 'N/A',
      //         });
      //     }
      // } catch (error) {
      //     const errorMessage = error.response?.data?.msg || error.message || 'Erro desconhecido';
      //     toast.error(`Erro inesperado ao buscar imagens do veículo #${vehicleToDelete.id}: ${errorMessage}`, { id: toastId });
      //     await logError({
      //         email: user?.email || 'N/A',
      //         log_erro: `Erro inesperado na busca das imagens do veículo #${vehicleToDelete.id}: ${errorMessage}`,
      //         formulario: location?.pathname || 'N/A',
      //     });
      //     hasCriticalError = true;
      // }

      // 2. APAGANDO FOTOS NO FIREBASE
      // if (!hasCriticalError && imagesToDeleteBackend.length > 0) {
      //     try {
      //         toast.loading('Excluindo imagens na nuvem...', { id: toastId });
      //         const results = await Promise.allSettled(
      //             imagesToDeleteBackend.map(async (image) => {
      //                 await Photos.deleteImage(image.storage_path, image.nome);
      //             })
      //         );

      //         const failedDeletions = results.filter(r => r.status === 'rejected');
      //         if (failedDeletions.length > 0) {
      //             hasCriticalError = true;
      //             const errorMessage = `Erro ao deletar ${failedDeletions.length} imagens na nuvem.`;
      //             toast.error(errorMessage, { id: toastId });
      //             await logError({
      //                 email: user?.email || 'N/A',
      //                 log_erro: `Falha ao excluir imagens no Firebase, Pasta: ${imagesToDeleteBackend[0].storage_path} do Veículo ${vehicleToDelete.id}: ${JSON.stringify(failedDeletions)}`,
      //                 formulario: location.pathname || 'N/A',
      //             });
      //         } else {
      //             toast.success('Imagens excluídas na nuvem com sucesso!', { id: toastId });
      //         }
      //     } catch (error) {
      //         hasCriticalError = true;
      //         const errorMessage = error.response?.data?.msg || error.message || 'Erro ao deletar imagens na nuvem.';
      //         toast.error(errorMessage, { id: toastId });
      //         await logError({
      //             email: user?.email || 'N/A',
      //             log_erro: `Erro inesperado ao excluir imagens, pasta: ${imagesToDeleteBackend[0].storage_path} do veículo #${vehicleToDelete.id}: ${errorMessage}`,
      //             formulario: location.pathname || 'N/A',
      //         });
      //     }
      // } else {
      //     toast.success('Nenhuma imagem para excluir na nuvem', { id: toastId });
      // }

      // 1. APAGANDO IMAGENS NO FTP E BANCO
      try {
        toast.loading("Excluindo imagens...", { id: toastId });
        await api.delete(`/api/imagens-ftp/${vehicleToDelete.id}`);
        toast.success("Imagens apagadas com sucesso!");
      } catch (errImg) {
        hasCriticalError = true;
        const errorMessage =
          errImg.response?.data?.msg || errImg.message || "Erro desconhecido";
        toast.error(
          `Erro inesperado ao excluir imagens do veículo #${vehicleToDelete.id}: ${errorMessage}`,
          { id: toastId },
        );
        await logError({
          email: user?.email || "N/A",
          log_erro: `Erro inesperado ao excluir imagens do veículo #${vehicleToDelete.id}: ${errorMessage}`,
          formulario: location.pathname || "N/A",
        });
      }

      // 3. APAGANDO REGISTROS DE IMAGENS NO BANCO
      // if (!hasCriticalError) {
      //     if (imagesToDeleteBackend.length > 0) {
      //         try {
      //             toast.loading('Excluindo registros de imagens no banco...', { id: toastId });
      //             const result = await api.delete('/api/imagens', { data: { imagesToDelete: imagesToDeleteBackend } });
      //             if (result.status !== 204) {
      //                 hasCriticalError = true;
      //                 const errorMessage = result.response?.data?.msg || result.message || 'Erro desconhecido ao deletar imagens do banco.';
      //                 toast.error(`Erro ao deletar imagens do banco de dados: ${errorMessage}`, { id: toastId });
      //                 await logError({
      //                     email: user?.email || 'N/A',
      //                     log_erro: `Erro ao deletar registros de imagens do veículo #${vehicleToDelete.id}: ${errorMessage}`,
      //                     formulario: location.pathname || 'N/A',
      //                 });
      //             }

      //             toast.success('Imagens excluídas no banco com sucesso!', { id: toastId });
      //         } catch (errorDeleteDbImg) {
      //             hasCriticalError = true;
      //             const errorMessage = errorDeleteDbImg.response?.data?.msg || errorDeleteDbImg.message || 'Erro desconhecido';
      //             toast.error(`Erro inesperado ao excluir registros de imagens do veículo #${vehicleToDelete.id}: ${errorMessage}`, { id: toastId });
      //             await logError({
      //                 email: user?.email || 'N/A',
      //                 log_erro: `Erro inesperado ao excluir registros de imagens do veículo #${vehicleToDelete.id}: ${errorMessage}`,
      //                 formulario: location.pathname || 'N/A',
      //             });
      //         }
      //     }
      // }

      // 2. APAGANDO REGISTRO DE VIEWS
      if (!hasCriticalError) {
        toast.loading("Excluindo visualizações do veículo...", { id: toastId });
        // VERIFICANDO SE HA VIEWS
        try {
          const response = await api.get(
            `/api/visualizacoes/${vehicleToDelete.id}`,
          );
          if (response.data === 0) {
            toast.success("Nenhuma visualização para excluir", { id: toastId });
            hasViews = false;
          }
        } catch (errorSearchViews) {
          hasCriticalError = true;
          const errorMessage =
            errorSearchViews.response?.data?.msg ||
            errorSearchViews.message ||
            "Erro desconhecido.";
          toast.error(
            `Erro inesperado ao buscar registros de visualizações: ${errorMessage}`,
            { id: toastId },
          );
        }
      }
      if (!hasCriticalError) {
        if (hasViews) {
          try {
            const result = await api.delete(
              `/api/visualizacoes/${vehicleToDelete.id}`,
            );
            if (result.status !== 204) {
              hasCriticalError = true;
              const errorMessage =
                result.response?.data?.msg ||
                result.message ||
                "Erro desconhecido";
              toast.error(`Erro ao deletar visualizações: ${errorMessage}`, {
                id: toastId,
              });
              await logError({
                email: user?.email || "N/A",
                log_erro: `Erro ao deletar visualizações do veículo #${vehicleToDelete.id}: ${errorMessage}`,
                formulario: location.pathname || "N/A",
              });
            }

            toast.success("Visualizacoes excluídas com sucesso!", {
              id: toastId,
            });
          } catch (errorDeleteViews) {
            hasCriticalError = true;
            const errorMessage =
              errorDeleteViews.response?.data?.msg ||
              errorDeleteViews.message ||
              "Erro desconhecido.";
            toast.error(
              `Erro inesperado ao tentar excluir registros de visualizações: ${errorMessage}`,
              { id: toastId },
            );
            await logError({
              email: user?.email || "N/A",
              log_erro: `Erro inesperado ao excluir registros de visualizações do veículo #${vehicleToDelete.id}: ${errorMessage}`,
              formulario: location.pathname || "N/A",
            });
          }
        }
      }

      // 3. APAGANDO REGISTRO DE VEICULO NO BANCO
      if (!hasCriticalError) {
        try {
          toast.loading("Excluindo veículo...", { id: toastId });
          const result = await api.delete(`/api/anuncio/${vehicleToDelete.id}`);
          if (result.status !== 204) {
            const errorMessage =
              result.response?.data?.msg ||
              result.message ||
              "Erro desconhecido.";
            toast.error(
              `Erro ao excluir veículo #${vehicleToDelete.id}: ${errorMessage}`,
              { id: toastId },
            );
            await logError({
              email: user?.email || "N/A",
              log_erro: `Erro ao excluir veículo #${vehicleToDelete.id}: ${errorMessage}`,
              formulario: location?.pathname || "N/A",
            });
          }

          // ATUALIZANDO LISTA DE VEICULOS
          setOfferList((prevList) =>
            prevList.filter((vehicle) => vehicle.id !== vehicleToDelete.id),
          );
          toast.success("Veículo excluido com sucesso!", { id: toastId });
          await logUsuarioForm({
            email: user?.email || "N/A",
            formulario: `/dashboard/anuncio/${vehicleToDelete.id}/deletar`,
          });
        } catch (error) {
          const errorMessage =
            error.response?.data?.msg ||
            error.message ||
            "Erro desconhecido ao excluir veículo.";
          toast.error(`Erro inesperado ao excluir veículo: ${errorMessage}`, {
            id: toastId,
          });
          await logError({
            email: user?.email || "N/A",
            log_erro: `Erro inesperado na exclusão do veículo #${vehicleToDelete.id}: ${errorMessage}`,
            formulario: location?.pathname || "N/A",
          });
        }
      }
    }
  };

  return (
    <>
      <div className="row dashboardBoxWrapperRow">
        <div className="col-md-8">
          <div className="dashboardBoxWrapper d-flex">
            <DashboardBox
              color={["#1DA256", "#48D483"]}
              icon={<MdOutlineTrendingUp />}
              title={"Total de visualizações em anúncios"}
              apiPath={"/api/visualizacoes/total"}
              renderValue={(data) => (
                <span className="text-white">
                  {data.total_visualizacoes !== undefined
                    ? data.total_visualizacoes
                    : "N/A"}
                </span>
              )}
            />
            <DashboardBox
              color={["#C012E2", "#EB64FE"]}
              icon={<MdOutlineStar />}
              title={"Anúncio mais visualizado"}
              apiPath={"/api/visualizacoes/top-primeiro"}
              renderValue={(data) => {
                if (!data) return "N/A";
                if (data.veiculo_id !== undefined) {
                  return (
                    <span
                      className="text-white"
                      style={{ fontSize: "18px", lineHeight: 1 }}
                    >
                      ID: {data.veiculo_id}
                      <br /> Visualizações: {data.total_visualizacoes || 0}
                    </span>
                  );
                }
                return "Nenhum"; // Caso não haja veículo mais visualizado
              }}
            />
            <DashboardBox
              color={["#2C78E5", "#60AFF5"]}
              icon={<MdPointOfSale />}
              title={"Total de anúncios ativos"}
              disabledBtn={true}
              apiPath={"/api/anuncios/total-anuncios-ativos/dashboard"}
              renderValue={(data) => (
                <span className="text-white">
                  {data.anuncios_ativos !== undefined
                    ? data.anuncios_ativos
                    : "N/A"}
                </span>
              )}
            />
            <DashboardBox
              color={["#E1950E", "#F3CD29"]}
              icon={<MdLocalGroceryStore />}
              title={"Total de ofertas ativas"}
              disabledBtn={true}
              apiPath={"/api/anuncios/total-ofertas-ativas/dashboard"}
              renderValue={(data) => (
                <span className="text-white">
                  {data.ofertas_ativas !== undefined
                    ? data.ofertas_ativas
                    : "N/A"}
                </span>
              )}
            />
          </div>
        </div>
        <div className="col-md-4 ps-0 topPart2">
          {loadingGraph && !errorChartData && (
            <div className="box graphBox d-flex align-items-center justify-content-center">
              <CircularProgress sx={{ color: "white" }} />
            </div>
          )}
          {!loadingGraph && (
            <div className="box graphBox">
              <div className="d-flex align-items-center w-100">
                <h6 className="text-white">Anúncios com mais visualizações</h6>
                <div className="ms-auto">
                  <Button className="toggleIcon" onClick={handleClickGraph}>
                    <MdMoreVert />
                  </Button>
                  <Menu
                    MenuListProps={{
                      "aria-labelledby": "long-button",
                    }}
                    anchorEl={anchorEl}
                    open={openGraph}
                    onClose={handleCloseGraph}
                    slotProps={{
                      paper: {
                        style: {
                          maxHeight: ITEM_HEIGHT * 4.5,
                          width: "20ch",
                        },
                      },
                    }}
                  >
                    <MenuItem onClick={() => handleMenuItemClick(1)}>
                      <MdOutlineUpdate style={{ marginRight: "10px" }} /> Último
                      Dia
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick(2)}>
                      <MdOutlineUpdate style={{ marginRight: "10px" }} /> Última
                      Semana
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick(3)}>
                      <MdOutlineUpdate style={{ marginRight: "10px" }} /> Último
                      Mês
                    </MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick(4)}>
                      <MdOutlineUpdate style={{ marginRight: "10px" }} /> Último
                      Ano
                    </MenuItem>
                  </Menu>
                </div>
              </div>

              {errorChartData && (
                <Alert severity="error">Erro: {errorChartData}</Alert>
              )}
              {!errorChartData && chartData.length > 0 ? (
                <>
                  <h3 className="text-white font-weight-bold">Total de</h3>
                  <p className="text-white">
                    {totalViewsSum > 1
                      ? `${totalViewsSum} visualizações`
                      : `${totalViewsSum} visualização`}
                    {selectedOption === 1 && " " + PERIOD_OPTIONS[0].label}
                    {selectedOption === 2 && " " + PERIOD_OPTIONS[1].label}
                    {selectedOption === 3 && " " + PERIOD_OPTIONS[2].label}
                    {selectedOption === 4 && " " + PERIOD_OPTIONS[3].label}
                  </p>
                  <PieChart
                    series={[
                      {
                        data: chartData,
                      },
                    ]}
                    onItemClick={handlePieItemClick}
                    width={200}
                    height={170}
                  />
                </>
              ) : (
                !errorChartData && (
                  <p className="text-white">
                    Nenhum dado disponível para o período
                  </p>
                )
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card shadow border-0 p-3 mt-4">
        <h3 className="hd">Anúncios em oferta</h3>
        {loading && !errorOfferList && (
          <div className="d-flex w-100 align-items-center justify-content-center">
            <CircularProgress />
          </div>
        )}
        {!loading && errorOfferList && (
          <Alert severity="error">{errorOfferList}</Alert>
        )}
        {!loading && !errorOfferList && offerList.length === 0 && (
          <p>Nenhuma oferta encontrada</p>
        )}
        {!loading && !errorOfferList && offerList.length > 0 && (
          <Paper sx={{ height: 400, width: "100%" }}>
            <DataGrid
              rows={offerList}
              columns={columns}
              paginationModel={paginationModel} // Controlado pelo estado
              onPaginationModelChange={setPaginationModel} // Callback para atualizar o estado
              pageSizeOptions={pageSizeOptions}
              paginationMode="server" // <--- Indica que a paginação é no servidor
              rowCount={rowCount} // <--- Passa o total de linhas para o DataGrid
              // disableRowSelectionOnClick
              localeText={localeText}
              sx={{
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "#1976d2",
                  color: "white",
                },
              }}
            />
          </Paper>
        )}
      </div>
    </>
  );
}

export default DashboardHome;
